class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.refreshContents().finally(() => this.open(cartLink));
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  refreshPromise = undefined;

  async refreshContents() {
    if (!routes?.cart_url) return;

    if (this.refreshPromise) return this.refreshPromise;

    this.refreshPromise = Promise.all([
      fetch(`${routes.cart_url}?section_id=cart-drawer`),
      fetch(`${routes.cart_url}?section_id=cart-icon-bubble`),
    ])
      .then(([drawerResponse, iconResponse]) => Promise.all([drawerResponse.text(), iconResponse.text()]))
      .then(([drawerText, iconText]) => {
        const html = new DOMParser().parseFromString(drawerText, 'text/html');
        const sourceDrawer = html.querySelector('cart-drawer');
        const sourceInner = html.querySelector('#CartDrawer .drawer__inner');
        const targetInner = this.querySelector('.drawer__inner');

        if (!sourceInner || !targetInner) return;

        if (sourceDrawer) {
          this.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
        }

        this.syncDrawerElement(targetInner, sourceInner, '.drawer__inner-empty');
        this.syncDrawerElement(targetInner, sourceInner, 'cart-drawer-items');
        this.syncDrawerElement(targetInner, sourceInner, 'product-recommendations.kitchvo-cart-suggest');
        this.syncDrawerElement(targetInner, sourceInner, '.drawer__footer');

        const iconHtml = new DOMParser().parseFromString(iconText, 'text/html');
        const sourceIcon = iconHtml.querySelector('.shopify-section');
        const targetIcon = document.getElementById('cart-icon-bubble');
        if (sourceIcon && targetIcon) {
          targetIcon.innerHTML = sourceIcon.innerHTML;
        }

        this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
        this.loadCartRecommendations();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.refreshPromise = undefined;
      });

    return this.refreshPromise;
  }

  syncDrawerElement(parent, sourceParent, selector) {
    const target = parent.querySelector(selector);
    const source = sourceParent.querySelector(selector);

    if (source && target) {
      target.replaceWith(source.cloneNode(true));
      return;
    }

    if (source && !target) {
      const footer = parent.querySelector('.drawer__footer');
      const clone = source.cloneNode(true);
      if (footer) {
        footer.before(clone);
      } else {
        parent.appendChild(clone);
      }
      return;
    }

    if (!source && target) {
      target.remove();
    }
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
    this.loadCartRecommendations();

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.loadCartRecommendations();
      this.open();
    });
  }

  loadCartRecommendations() {
    const cartRecommendations = this.querySelector('product-recommendations.kitchvo-cart-suggest');
    if (!cartRecommendations) return;

    if (this.classList.contains('is-empty')) {
      cartRecommendations.innerHTML = '';
      cartRecommendations.removeAttribute('data-product-id');
      cartRecommendations.classList.remove('product-recommendations--loaded');
      return;
    }

    const productId = cartRecommendations.dataset.productId;
    if (!productId) {
      cartRecommendations.innerHTML = '';
      cartRecommendations.classList.remove('product-recommendations--loaded');
      return;
    }

    if (typeof cartRecommendations.initializeRecommendations === 'function') {
      cartRecommendations.initializeRecommendations(productId);
      return;
    }

    cartRecommendations.loadRecommendations(productId);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
